import { useState, useMemo, useEffect } from "react";
import "./day-plan-styles/DayPlanParent.css";
import StatusColumn from "./day-plan-components/StatusColumn";
import Modal from "../modal-component/Modal";
import TaskCompletionPrompt from "./day-plan-components/day-plan-modals/TaskCompletionPrompt";
import WarningPrompt from "./day-plan-components/day-plan-modals/WarningPrompt";
import ProductionBreakdown from "./day-plan-components/day-plan-modals/ProductionBreakdown";
import { useDataContext } from "../../data-context/DataContextProvider";
import { useTranslation } from "@preppa/translation";
import {
  generateTimeSegments,
  distributeHourlyCardsToBuckets,
} from "../../utils/timeSegmentation";
import { PlanData, HourlyTaskCard, TASK_STATUS } from "../../types/types";
import {
  getStatusFromCode,
  canTransition,
  getTransitionModalType,
  TransitionModalType,
  getTotalProducedVolume,
  getStatusCode,
  getDisplayConversion,
  hasProducedInput,
  hasAvailableInput,
} from "../../utils/planDataHelpers";
import { getPlanDayForWorksheet } from "../../utils/dateUtils";
import { updatePlanData } from "../../api/apiCallsPlanData";

type PendingTransition = {
  task: PlanData;
  hourlyCard?: HourlyTaskCard;
  from: string;
  to: string;
};

function HourlyPlan() {
  const {
    selectedDayView,
    planData,
    selectedWorksheet,
    selectedDate,
    buCode,
    buType,
    dispatch,
  } = useDataContext();
  const { translate } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<TransitionModalType>("none");
  const [pendingTransition, setPendingTransition] =
    useState<PendingTransition | null>(null);

  const [expandedTodoIndex, setExpandedTodoIndex] = useState<number | null>(
    null,
  );
  const [expandedInProgressIndex, setExpandedInProgressIndex] = useState<
    number | null
  >(null);
  const [expandedDoneIndex, setExpandedDoneIndex] = useState<number | null>(
    null,
  );

  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [breakdownPlanData, setBreakdownPlanData] = useState<PlanData | null>(null);

  const [hourlyCardStatuses, setHourlyCardStatuses] = useState<
    Map<string, string>
  >(new Map());

  useEffect(() => {
    if (!selectedWorksheet) {
      setHourlyCardStatuses(new Map());
      return;
    }

    const selectedPlanDay = getPlanDayForWorksheet(
      selectedDate,
      selectedWorksheet.prepDayBefore,
    );
    const dayPlanData = planData.find((day) => day.planDay === selectedPlanDay);

    if (dayPlanData && dayPlanData.dayPlanDatas.length > 0) {
      const initialHourlyStatuses = new Map<string, string>();

      dayPlanData.dayPlanDatas.forEach((item) => {
        if (
          item.worksheetData &&
          item.worksheetData.id === selectedWorksheet.id
        ) {
          item.itemSalesPlanDtos.forEach((dto) => {
            const hour = parseInt(dto.plannedDayHour.substring(8, 10), 10);
            if (!isNaN(hour)) {
              const statusCode = dto.statusCode || "10";
              const status = getStatusFromCode(statusCode);
              const key = `${item.itemCode}-${hour}`;
              initialHourlyStatuses.set(key, status);
            }
          });
        }
      });

      setHourlyCardStatuses(initialHourlyStatuses);
    } else {
      setHourlyCardStatuses(new Map());
    }
  }, [planData, selectedWorksheet, selectedDate]);

  const timeSegments = useMemo(() => {
    return generateTimeSegments(selectedDayView);
  }, [selectedDayView]);

  const allHourlyCards = useMemo(() => {
    if (!selectedWorksheet) return [];

    const shouldIncludeByPlanRules = (
      plannedVol: number | null,
      forecastVol: number | null,
    ): boolean => {
      if (plannedVol === null || plannedVol === undefined) {
        return (forecastVol ?? 0) > 0;
      }
      return plannedVol > 0;
    };

    const selectedPlanDay = getPlanDayForWorksheet(
      selectedDate,
      selectedWorksheet.prepDayBefore,
    );
    const dayPlanData = planData.find((day) => day.planDay === selectedPlanDay);

    if (!dayPlanData) return [];

    const cards: HourlyTaskCard[] = [];

    dayPlanData.dayPlanDatas.forEach((item) => {
      if (
        item.worksheetData &&
        item.worksheetData.id === selectedWorksheet.id
      ) {
        item.itemSalesPlanDtos.forEach((itemSalesPlan) => {
          const hour = parseInt(
            itemSalesPlan.plannedDayHour.substring(8, 10),
            10,
          );
          if (
            !isNaN(hour) &&
            shouldIncludeByPlanRules(
              itemSalesPlan.plannedVol,
              itemSalesPlan.forecastVol,
            )
          ) {
            cards.push({
              hour,
              itemSalesPlan,
              parentPlanData: item,
              fromHour: itemSalesPlan.plannedDayHour,
              toHour: itemSalesPlan.plannedDayHour,
            });
          }
        });
      }
    });

    return cards;
  }, [planData, selectedWorksheet, selectedDate]);

  const activeHours = useMemo(() => {
    return new Set(allHourlyCards.map(card => card.hour));
  }, [allHourlyCards]);

  const getHourlyCardKey = (card: HourlyTaskCard): string => {
    return `${card.parentPlanData.itemCode}-${card.hour}`;
  };

  const hourlyTodoBuckets = useMemo(() => {
    const todoCards = allHourlyCards.filter((card) => {
      const key = getHourlyCardKey(card);
      return hourlyCardStatuses.get(key) === TASK_STATUS.TODO;
    });
    return distributeHourlyCardsToBuckets(todoCards, timeSegments);
  }, [allHourlyCards, hourlyCardStatuses, timeSegments]);

  const hourlyInProgressBuckets = useMemo(() => {
    const inProgressCards = allHourlyCards.filter((card) => {
      const key = getHourlyCardKey(card);
      return hourlyCardStatuses.get(key) === TASK_STATUS.IN_PROGRESS;
    });
    const buckets = new Map<string, HourlyTaskCard[]>();
    buckets.set("all", inProgressCards);
    return buckets;
  }, [allHourlyCards, hourlyCardStatuses]);

  const hourlyDoneBuckets = useMemo(() => {
    const doneCards = allHourlyCards.filter((card) => {
      const key = getHourlyCardKey(card);
      return hourlyCardStatuses.get(key) === TASK_STATUS.DONE;
    });
    const buckets = new Map<string, HourlyTaskCard[]>();
    buckets.set("all", doneCards);
    return buckets;
  }, [allHourlyCards, hourlyCardStatuses]);

  function updateHourlyTaskStatus(
    card: HourlyTaskCard,
    from: string,
    to: string,
  ): string {
    const key = getHourlyCardKey(card);
    const previousStatus = hourlyCardStatuses.get(key) || from;

    setHourlyCardStatuses((prevStatuses) => {
      const newStatuses = new Map(prevStatuses);
      newStatuses.set(key, to);
      return newStatuses;
    });

    return previousStatus;
  }

  function revertHourlyTaskStatus(
    card: HourlyTaskCard,
    previousStatus: string,
  ): void {
    const key = getHourlyCardKey(card);

    setHourlyCardStatuses((prevStatuses) => {
      const newStatuses = new Map(prevStatuses);
      newStatuses.set(key, previousStatus);
      return newStatuses;
    });
  }

  async function handleHourlyStatusTransition(
    card: HourlyTaskCard,
    from: string,
    to: string,
  ) {
    if (!canTransition(from, to)) {
      console.warn(`Transition from ${from} to ${to} is not allowed`);
      return;
    }

    const task = card.parentPlanData;
    const itemSalesPlan = card.itemSalesPlan;
    const producedVol = hasProducedInput(task) ? (itemSalesPlan.producedVol || 0) : 0;
    const availableVol = hasAvailableInput(task) ? (itemSalesPlan.availableVol || 0) : 0;
    const plannedVol = itemSalesPlan.plannedVol || itemSalesPlan.forecastVol || 0;

    const modalTypeNeeded = getTransitionModalType(
      from,
      to,
      producedVol,
      availableVol,
      plannedVol,
    );

    if (modalTypeNeeded === "blocked") {
      return;
    }

    const effectiveModalType = (modalTypeNeeded === "produceQuantity" && !hasProducedInput(task))
      ? "none"
      : modalTypeNeeded;

    if (effectiveModalType === "none") {
      const previousStatus = updateHourlyTaskStatus(card, from, to);
      const success = await callHourlyStatusUpdateAPI(card, to);
      if (!success) {
        revertHourlyTaskStatus(card, previousStatus);
      }
    } else {
      setPendingTransition({ task, hourlyCard: card, from, to });
      setModalType(effectiveModalType);
      setIsModalOpen(true);
    }
  }

  const callHourlyStatusUpdateAPI = async (
    card: HourlyTaskCard,
    newStatus: string,
    producedVol?: number,
    availableVol?: number,
  ): Promise<boolean> => {
    const statusCode = getStatusCode(newStatus);

    const itemSalesPlan = card.itemSalesPlan;
    const finalProducedVol =
      producedVol !== undefined ? producedVol : itemSalesPlan.producedVol || 0;
    const finalAvailableVol =
      availableVol !== undefined
        ? availableVol
        : itemSalesPlan.availableVol || 0;
    const comment = itemSalesPlan.comment || "";

    return await updatePlanData({
      itemCode: card.parentPlanData.itemCode,
      fromHour: card.fromHour,
      toHour: card.toHour,
      buCode,
      buType,
      statusCode,
      producedVol: finalProducedVol,
      availableVol: finalAvailableVol,
      comment,
      callBack: dispatch,
    });
  };
  

  function getHourlyCardEditability(card: HourlyTaskCard): {
    isEditable: boolean;
    updateHour: string;
  } {
    return { isEditable: true, updateHour: card.hour.toString() };
  }

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setPendingTransition(null);
    setModalType("none");
  };
  const handleProduceQtyConfirm = async (producedQty: number) => {
    if (!pendingTransition) return;

    const { hourlyCard, from, to } = pendingTransition;

    handleModalCancel();

    if (hourlyCard) {
      const previousStatus = updateHourlyTaskStatus(hourlyCard, from, to);
      const success = await callHourlyStatusUpdateAPI(
        hourlyCard,
        to,
        producedQty,
      );
      if (!success) {
        revertHourlyTaskStatus(hourlyCard, previousStatus);
      }
    }
  };

  const handleWarningConfirm = async () => {
    if (!pendingTransition) return;

    const { hourlyCard, from, to } = pendingTransition;

    handleModalCancel();
    if (hourlyCard) {
      const previousStatus = updateHourlyTaskStatus(hourlyCard, from, to);
      const success = await callHourlyStatusUpdateAPI(
        hourlyCard,
        to,
        0,
        hasAvailableInput(hourlyCard.parentPlanData) ? (hourlyCard.itemSalesPlan.availableVol || 0) : 0,
      );
      if (!success) {
        revertHourlyTaskStatus(hourlyCard, previousStatus);
      }
    }
  };

  const handleExpansionToggleTodo = (index: number) => {
    setExpandedTodoIndex(expandedTodoIndex === index ? null : index);
  };

  const handleExpansionToggleInProgress = (index: number) => {
    setExpandedInProgressIndex(
      expandedInProgressIndex === index ? null : index,
    );
  };

  const handleExpansionToggleDone = (index: number) => {
    setExpandedDoneIndex(expandedDoneIndex === index ? null : index);
  };

  const handleViewBreakdown = (planData: PlanData) => {
    setBreakdownPlanData(planData);
    setIsBreakdownOpen(true);
  };

  const handleBreakdownClose = () => {
    setIsBreakdownOpen(false);
    setBreakdownPlanData(null);
  };

  return (
    <div id="day-plan-container">
      <div className="task-status-wrapper">
        <StatusColumn
          status={TASK_STATUS.TODO}
          label={translate("dayPlan.status.todo")}
          hourlyBuckets={hourlyTodoBuckets}
          timeSegments={timeSegments}
          activeHours={activeHours}
          expandedIndex={expandedTodoIndex}
          onToggleExpand={handleExpansionToggleTodo}
          onHourlyStatusChange={handleHourlyStatusTransition}
          getHourlyCardEditability={getHourlyCardEditability}
          onViewBreakdown={handleViewBreakdown}
        />
        <StatusColumn
          status={TASK_STATUS.IN_PROGRESS}
          label={translate("dayPlan.status.inProgress")}
          hourlyBuckets={hourlyInProgressBuckets}
          timeSegments={[]}
          expandedIndex={expandedInProgressIndex}
          onToggleExpand={handleExpansionToggleInProgress}
          onHourlyStatusChange={handleHourlyStatusTransition}
          getHourlyCardEditability={getHourlyCardEditability}
          onViewBreakdown={handleViewBreakdown}
        />
        <StatusColumn
          status={TASK_STATUS.DONE}
          label={translate("dayPlan.status.done")}
          hourlyBuckets={hourlyDoneBuckets}
          timeSegments={[]}
          expandedIndex={expandedDoneIndex}
          onToggleExpand={handleExpansionToggleDone}
          onHourlyStatusChange={handleHourlyStatusTransition}
          getHourlyCardEditability={getHourlyCardEditability}
          onViewBreakdown={handleViewBreakdown}
        />
      </div>
      <Modal isModalOpen={isModalOpen} onClose={handleModalCancel}>
        {modalType === "produceQuantity" && pendingTransition && (() => {
          const pd = pendingTransition.hourlyCard?.parentPlanData ?? pendingTransition.task;
          const dc = getDisplayConversion(pd);
          const cf = dc?.conversionFactor ?? 1;
          const uom = dc ? dc.displayUom : (
            pendingTransition.hourlyCard
              ? pendingTransition.hourlyCard.itemSalesPlan.uomCode || ""
              : pendingTransition.task.itemSalesPlanDtos[0]?.uomCode || ""
          );
          return (
          <TaskCompletionPrompt
            initialProducedQty={
              pendingTransition.hourlyCard
                ? pendingTransition.hourlyCard.itemSalesPlan.producedVol || 0
                : getTotalProducedVolume(pendingTransition.task)
            }
            uomCode={uom}
            conversionFactor={cf}
            originStatus={pendingTransition.from}
            totalPlannedVol={
              pendingTransition.hourlyCard &&
              pendingTransition.hourlyCard.itemSalesPlan.plannedVol > 0
                ? pendingTransition.hourlyCard.itemSalesPlan.plannedVol
                : pendingTransition.hourlyCard?.itemSalesPlan.forecastVol || 0
            }
            availableVol={
              pendingTransition.hourlyCard
                ? (hasAvailableInput(pendingTransition.hourlyCard.parentPlanData) ? (pendingTransition.hourlyCard.itemSalesPlan.availableVol || 0) : 0)
                : (hasAvailableInput(pendingTransition.task) ? (pendingTransition.task.itemSalesPlanDtos[0]?.availableVol || 0) : 0)
            }
            onConfirm={handleProduceQtyConfirm}
            onCancel={handleModalCancel}
          />
          );
        })()}
        {modalType === "warning" && pendingTransition && (
          <WarningPrompt
            message={translate("dayPlan.prompts.warningMoveBackToTodo")}
            onConfirm={handleWarningConfirm}
            onCancel={handleModalCancel}
          />
        )}
      </Modal>
      <Modal isModalOpen={isBreakdownOpen} onClose={handleBreakdownClose}>
        {breakdownPlanData && (
          <ProductionBreakdown
            planData={breakdownPlanData}
            onClose={handleBreakdownClose}
          />
        )}
      </Modal>
    </div>
  );
}

export default HourlyPlan;
